import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { contractTitle } from '../utils/contractTitle';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi, contractApi } from '../services/api';
import type {
  AdminAuditEvent,
  AdminAuditEventQuery,
  AdminCustomer,
  AdminProject,
  Company,
  Contract,
} from '../types';

const eventLabelKeys: Record<string, string> = {
  'ping.recipient.onboarding_requested': 'adminAuditTrail.events.pingRecipientOnboardingRequested',
  'ping.recipient.observed': 'adminAuditTrail.events.pingRecipientObserved',
  'ping.deposit.created': 'adminAuditTrail.events.pingDepositCreated',
  'ping.deposit.step_changed': 'adminAuditTrail.events.pingDepositStepChanged',
  'ping.deposit.document_changed': 'adminAuditTrail.events.pingDepositDocumentChanged',
  'ping.deposit.settlement_requested': 'adminAuditTrail.events.pingDepositSettlementRequested',
  'ping.deposit.observed': 'adminAuditTrail.events.pingDepositObserved',
  'ping.payout.authorized': 'adminAuditTrail.events.pingPayoutAuthorized',
  'ping.payout.state_changed': 'adminAuditTrail.events.pingPayoutStateChanged',
  'ping.payout.step_requested': 'adminAuditTrail.events.pingPayoutStepRequested',
  'ping.payout.debit_verified': 'adminAuditTrail.events.pingPayoutDebitVerified',
  'ping.payout.observed': 'adminAuditTrail.events.pingPayoutObserved',
  'onboarding.profile_saved': 'adminAuditTrail.events.onboardingProfileSaved',
  'onboarding.company_saved': 'adminAuditTrail.events.onboardingCompanySaved',
  'terms.accepted': 'adminAuditTrail.events.termsAccepted',
  'bankid.verified': 'adminAuditTrail.events.bankidVerified',
  'impersonation.requested': 'adminAuditTrail.events.impersonationRequested',
  'impersonation.started': 'adminAuditTrail.events.impersonationStarted',
  'impersonation.ended': 'adminAuditTrail.events.impersonationEnded',
  'impersonation.viewed': 'adminAuditTrail.events.impersonationViewed',
  'impersonation.write_blocked': 'adminAuditTrail.events.impersonationWriteBlocked',
  'onboarding.completed': 'adminAuditTrail.events.onboardingCompleted',
  'user.email_verified_by_invitation': 'adminAuditTrail.events.emailVerifiedByInvitation',
  'invitation.accepted': 'adminAuditTrail.events.invitationAccepted',
  'invitation.declined': 'adminAuditTrail.events.invitationDeclined',
  'project.customer_released': 'adminAuditTrail.events.projectCustomerReleased',
  'company.representative.created': 'adminAuditTrail.events.companyRepresentativeCreated',
  'contract.draft_sent': 'adminAuditTrail.events.contractDraftSent',
  'contract.draft_deleted': 'adminAuditTrail.events.contractDraftDeleted',
  'contract.draft_approved': 'adminAuditTrail.events.contractDraftApproved',
  'contract.draft_rejected': 'adminAuditTrail.events.contractDraftRejected',
  'contract.signed': 'adminAuditTrail.events.contractSigned',
  'milestone.attested': 'adminAuditTrail.events.milestoneAttested',
  'milestone.rejected': 'adminAuditTrail.events.milestoneRejected',
  'payment.policy_consent_recorded': 'adminAuditTrail.events.paymentPolicyConsentRecorded',
  'payment.received': 'adminAuditTrail.events.paymentReceived',
  'escrow.released': 'adminAuditTrail.events.escrowReleased',
  'payout.booked': 'adminAuditTrail.events.payoutBooked',
};

const formatDateTime = (value: string, locale: string) => {
  if (!value) return '';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const shortId = (value: string | null | undefined) =>
  value ? `${value.slice(0, 8)}...` : '';

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
type AuditIdFilter = 'userId' | 'projectId' | 'milestoneId' | 'paymentId' | 'correlationId';

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export function AdminAuditTrail() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<AdminAuditEvent[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [contractId, setContractId] = useState('');
  const [eventType, setEventType] = useState('');
  const [idFilterType, setIdFilterType] = useState<AuditIdFilter>('paymentId');
  const [idFilter, setIdFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCompany = companies.find((company) => company.id === companyId);

  const filteredCustomers = useMemo(() => {
    if (!selectedCompany) return customers;
    return customers.filter(
      (customer) => customer.companyNumber === selectedCompany.companyNumber,
    );
  }, [customers, selectedCompany]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      const project = projects.find((item) => item.id === contract.projectId);
      if (!project) return false;
      if (customerId && project.customerId !== customerId) return false;
      if (
        selectedCompany &&
        project.companyNumber !== selectedCompany.companyNumber
      ) {
        return false;
      }
      return true;
    });
  }, [contracts, customerId, projects, selectedCompany]);

  const query = useMemo<AdminAuditEventQuery>(
    () => ({
      companyId: companyId || undefined,
      customerId: customerId || undefined,
      contractId: contractId || undefined,
      eventType: eventType || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      ...(idFilter && uuidPattern.test(idFilter.trim())
        ? { [idFilterType]: idFilter.trim() }
        : {}),
      limit: 200,
    }),
    [companyId, contractId, customerId, eventType, fromDate, idFilter, idFilterType, toDate],
  );

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setEvents(await adminApi.getAuditEvents(query));
    } catch (err) {
      console.error('Failed to load audit events:', err);
      setError(t('adminAuditTrail.errors.load'));
    } finally {
      setIsLoading(false);
    }
  }, [query, t]);

  useEffect(() => {
    const loadStaticFilters = async () => {
      try {
        const [projectRows, contractRows] = await Promise.all([
          adminApi.getAllProjects(),
          contractApi.getAllContracts(),
        ]);
        setProjects(projectRows);
        setContracts(contractRows);
      } catch (err) {
        console.error('Failed to load audit filter data:', err);
      }
    };
    loadStaticFilters();
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(async () => {
      try {
        setCompanies(
          await adminApi.getCompanyList({
            q: companySearch.trim() || undefined,
            limit: 50,
          }),
        );
      } catch (err) {
        console.error('Failed to load audit company filter data:', err);
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [companySearch]);

  useEffect(() => {
    const timeout = window.setTimeout(async () => {
      try {
        setCustomers(
          await adminApi.getAllCustomers({
            q: customerSearch.trim() || undefined,
            limit: 50,
          }),
        );
      } catch (err) {
        console.error('Failed to load audit customer filter data:', err);
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [customerSearch]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (customerId && !filteredCustomers.some((item) => item.id === customerId)) {
      setCustomerId('');
    }
  }, [customerId, filteredCustomers]);

  useEffect(() => {
    if (contractId && !filteredContracts.some((item) => item.id === contractId)) {
      setContractId('');
    }
  }, [contractId, filteredContracts]);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const { blob, filename } = await adminApi.exportAuditEvents(query);
      downloadBlob(blob, filename);
    } catch (err) {
      console.error('Failed to export audit events:', err);
      setError(t('adminAuditTrail.errors.export'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <Breadcrumb
              items={[
                { label: t('adminAuditTrail.breadcrumb.home'), path: '/admin/home' },
                { label: t('adminAuditTrail.breadcrumb.reports'), path: '/admin/reports-menu' },
                { label: t('adminAuditTrail.breadcrumb.current') },
              ]}
            />
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h4 className="fw-bold mb-1">{t('adminAuditTrail.title')}</h4>
                <p className="text-muted mb-0">
                  {t('adminAuditTrail.subtitle')}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleExport}
                disabled={isExporting}
              >
                <i className="feather-download me-1"></i>
                {isExporting ? t('adminAuditTrail.actions.exporting') : t('adminAuditTrail.actions.exportCsv')}
              </button>
            </div>
          </div>
        </div>

        <Card className="mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">{t('adminAuditTrail.filters.company')}</label>
              <input
                className="form-control mb-2"
                type="search"
                value={companySearch}
                onChange={(event) => setCompanySearch(event.target.value)}
                placeholder={t('adminAuditTrail.filters.companySearchPlaceholder')}
              />
              <select
                className="form-select"
                value={companyId}
                onChange={(event) => {
                  setCompanyId(event.target.value);
                  setCustomerId('');
                  setContractId('');
                }}
              >
                <option value="">{t('adminAuditTrail.filters.allCompanies')}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.companyNumber} - {company.companyName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('adminAuditTrail.filters.customer')}</label>
              <input
                className="form-control mb-2"
                type="search"
                value={customerSearch}
                onChange={(event) => setCustomerSearch(event.target.value)}
                placeholder={t('adminAuditTrail.filters.customerSearchPlaceholder')}
              />
              <select
                className="form-select"
                value={customerId}
                onChange={(event) => {
                  setCustomerId(event.target.value);
                  setContractId('');
                }}
              >
                <option value="">{t('adminAuditTrail.filters.allCustomers')}</option>
                {filteredCustomers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customerNumber} - {customer.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('adminAuditTrail.filters.contract')}</label>
              <select
                className="form-select"
                value={contractId}
                onChange={(event) => setContractId(event.target.value)}
              >
                <option value="">{t('adminAuditTrail.filters.allContracts')}</option>
                {filteredContracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    #{contract.contractNumber} - {contractTitle(contract)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('adminAuditTrail.filters.event')}</label>
              <select
                className="form-select"
                value={eventType}
                onChange={(event) => setEventType(event.target.value)}
              >
                <option value="">{t('adminAuditTrail.filters.allEvents')}</option>
                {Object.entries(eventLabelKeys).map(([value, labelKey]) => (
                  <option key={value} value={value}>
                    {t(labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('adminAuditTrail.filters.from')}</label>
              <input
                className="form-control"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('adminAuditTrail.filters.to')}</label>
              <input
                className="form-control"
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="audit-id-filter">{t('adminAuditTrail.filters.idFilterLabel')}</label>
              <div className="input-group">
                <select
                  className="form-select"
                  aria-label={t('adminAuditTrail.filters.idTypeAriaLabel')}
                  value={idFilterType}
                  onChange={(event) => setIdFilterType(event.target.value as AuditIdFilter)}
                >
                  <option value="paymentId">{t('adminAuditTrail.filters.idTypePayment')}</option>
                  <option value="correlationId">{t('adminAuditTrail.table.correlationId')}</option>
                  <option value="userId">{t('adminAuditTrail.filters.idTypeUser')}</option>
                  <option value="projectId">{t('adminAuditTrail.filters.idTypeProject')}</option>
                  <option value="milestoneId">{t('adminAuditTrail.filters.idTypeMilestone')}</option>
                </select>
                <input
                  id="audit-id-filter"
                  className={`form-control ${idFilter && !uuidPattern.test(idFilter.trim()) ? 'is-invalid' : ''}`}
                  type="search"
                  value={idFilter}
                  onChange={(event) => setIdFilter(event.target.value)}
                  placeholder={t('adminAuditTrail.filters.uuidPlaceholder')}
                  aria-describedby="audit-id-filter-help"
                />
              </div>
              <div id="audit-id-filter-help" className={idFilter && !uuidPattern.test(idFilter.trim()) ? 'invalid-feedback d-block' : 'form-text'}>
                {idFilter && !uuidPattern.test(idFilter.trim())
                  ? t('adminAuditTrail.filters.invalidUuid')
                  : t('adminAuditTrail.filters.idFilterHelp')}
              </div>
            </div>
            <div className="col-md-6 d-flex justify-content-md-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setCompanyId('');
                  setCompanySearch('');
                  setCustomerId('');
                  setCustomerSearch('');
                  setContractId('');
                  setEventType('');
                  setIdFilterType('paymentId');
                  setIdFilter('');
                  setFromDate('');
                  setToDate('');
                }}
              >
                {t('adminAuditTrail.actions.clear')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={loadEvents}
                disabled={isLoading}
              >
                <i className="feather-refresh-cw me-1"></i>
                {t('adminAuditTrail.actions.refresh')}
              </button>
            </div>
          </div>
        </Card>

        {error && <div className="alert alert-danger">{error}</div>}

        <Card>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">{t('adminAuditTrail.table.heading')}</h5>
            <span className="text-muted small">{t('adminAuditTrail.table.rowCount', { count: events.length })}</span>
          </div>
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('adminAuditTrail.table.loading')}</span>
              </div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center text-muted py-5">
              {t('adminAuditTrail.table.empty')}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle d-block d-lg-table admin-audit-table">
                <thead>
                  <tr>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.time')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.event')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.actor')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.company')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.customer')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.project')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.contract')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.target')}</th>
                    <th className="px-2 text-break">{t('adminAuditTrail.table.columns.metadata')}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="d-block d-lg-table-row mb-3 mb-lg-0">
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.time')}</span>{formatDateTime(event.createdAt, i18n.language)}</td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.event')}</span>
                        <span className="fw-semibold">
                          {eventLabelKeys[event.eventType]
                            ? t(eventLabelKeys[event.eventType])
                            : event.eventType}
                        </span>
                        <div className="text-muted small">{event.eventType}</div>
                      </td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.actor')}</span>
                        {event.actorName ?? event.actorEmail ?? event.actorSystemId ?? ''}
                        {event.actorPersonaType ? (
                          <div className="text-muted small">{t(['person', 'company', 'inspector', 'lawyer', 'admin', 'support'].includes(event.actorPersonaType) ? `persona.${event.actorPersonaType}` : 'persona.user')}</div>
                        ) : event.actorUserId ? (
                          <div className="text-muted small">{shortId(event.actorUserId)}</div>
                        ) : null}
                      </td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.company')}</span>
                        {event.companyName ?? ''}
                        {event.companyNumber ? (
                          <div className="text-muted small">#{event.companyNumber}</div>
                        ) : null}
                      </td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.customer')}</span>
                        {event.customerName ?? ''}
                        {event.customerNumber ? (
                          <div className="text-muted small">#{event.customerNumber}</div>
                        ) : null}
                      </td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.project')}</span>{event.projectNumber ? `#${event.projectNumber}` : ''}</td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.contract')}</span>{event.contractNumber ? `#${event.contractNumber}` : ''}</td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.target')}</span>
                        <span className="badge bg-light text-dark">
                          {event.targetType}
                        </span>
                        <div className="text-muted small">{shortId(event.targetId)}</div>
                        {event.paymentId && event.paymentId !== event.targetId ? (
                          <div className="text-muted small">{t('adminAuditTrail.table.paymentLabel', { id: shortId(event.paymentId) })}</div>
                        ) : null}
                      </td>
                      <td className="d-block d-lg-table-cell text-break"><span className="d-lg-none fw-semibold me-2">{t('adminAuditTrail.table.columns.metadata')}</span>
                        {event.amountOre != null && event.currency && <div className="fw-semibold mb-1">
                          {new Intl.NumberFormat(i18n.language, { style: 'currency', currency: event.currency }).format(event.amountOre / 100)}
                        </div>}
                        {event.correlationId && <div className="small text-break mb-1">
                          {t('adminAuditTrail.table.correlationId')}: <code>{event.correlationId}</code>
                        </div>}
                        {event.providerReference && <div className="small text-break mb-1">
                          {t('adminAuditTrail.table.providerReference')}: <code>{event.providerReference}</code>
                        </div>}
                        <code className="small text-wrap">
                          {JSON.stringify(event.metadata ?? {})}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
